import { cloneElement, Fragment, isValidElement, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, User, Check } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import {
  Combobox,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxInput,
  ComboboxList,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  ComboboxSeparator,
} from '@deepguide-ai/ui/combobox';
import { Button } from '@deepguide-ai/ui/button';
import { Avatar, AvatarFallback } from '@deepguide-ai/ui/avatar';
import { Badge } from '@deepguide-ai/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@deepguide-ai/ui/hover-card';
import { authClient } from '@/lib/auth-client';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';
import { MemberAvatar } from '@/components/compliance/MemberAvatar';

interface AssignComboboxProps {
  statusId?: string;
  templateId?: string | null;
  currentAssigneeId?: string | null;
  trigger?: 'icon' | 'button';
  className?: string;
  assignee?: {
    id: string;
    name: string | null;
    email?: string | null;
    image: string | null;
    isServiceAccount?: boolean | null;
  } | null;
  /** Show assignee name and email next to avatar, or "Assign" if no assignee */
  showName?: boolean;
  /** Controls how much assignee text is shown next to the avatar. */
  assigneeDisplayVariant?: 'full' | 'compact';
  onValueChange?: (value: string | null) => void;
  /** Read-only mode - shows assignee but disables interaction */
  readOnly?: boolean;
  /** Current task status (e.g., NOT_STARTED, IN_PROGRESS, SUBMITTED) used for eligibility filtering */
  taskStatus?: string;
  /** Custom trigger content - when provided, replaces default trigger */
  children?: React.ReactNode;
  /** Popover content width (default 320px) */
  contentWidth?: number | string;
  /** Preferred side for the popover content (Radix may still flip on collision). */
  contentSide?: 'top' | 'right' | 'bottom' | 'left';
  /** Show the hover-card preview on member rows (default true) */
  showHoverCard?: boolean;
  /** Show the "No assignee" option (default true). */
  allowUnassigned?: boolean;
  /** Include VIEWER members in the picker (default false). */
  includeViewers?: boolean;
  /**
   * Hide the AI agent (Vera) from the picker. Set when the selection
   * carries human-only semantics — e.g. signing an attestation, where a
   * service account cannot stand in for the user's belief.
   */
  excludeAgent?: boolean;
  /**
   * Render the picker's popover as modal. Needed when AssignCombobox lives
   * INSIDE a Radix Dialog: the Dialog's scroll-lock (react-remove-scroll)
   * otherwise blocks wheel scrolling in the portaled, non-modal popover, so
   * the member list can't scroll. A modal popover installs its own
   * scroll-allow node and stays portaled (no Dialog `overflow-hidden`
   * clipping). Default false to preserve non-modal behavior on cards/pages.
   */
  modal?: boolean;
}

export function AssignCombobox({
  statusId,
  currentAssigneeId,
  trigger = 'icon',
  className,
  assignee: assigneeProp,
  showName = false,
  assigneeDisplayVariant = 'full',
  onValueChange,
  readOnly = false,
  taskStatus,
  children,
  contentWidth = '320px',
  contentSide,
  showHoverCard = true,
  allowUnassigned = true,
  includeViewers = false,
  excludeAgent = false,
  modal = false,
}: AssignComboboxProps) {
  // Get email prefix (before @)
  const getEmailPrefix = (email: string | null | undefined) => {
    if (!email) return null;
    return email.split('@')[0];
  };

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  // Get current user
  const { data: session } = authClient.useSession();
  const currentUserId = session?.user?.id;

  // Fetch workspace members
  const membersQuery = useQuery(
    trpc.workspaceMembers.list.queryOptions()
  );
  // Fetch Vera agent user
  const agentQuery = useQuery(
    trpc.agentActivity.getAgentUser.queryOptions()
  );
  const agentUser = agentQuery.data;

  const members = membersQuery.data;
  const assignee = assigneeProp === undefined
    ? members?.find((member) => member.userId === currentAssigneeId)?.user
    : assigneeProp;

  // Type helper for member with user
  type MemberWithUser = NonNullable<typeof members>[number];

  const getMemberKeywords = (member: MemberWithUser) =>
    [
      member.user?.name || '',
      member.user?.email || '',
      getEmailPrefix(member.user?.email) || '',
    ].filter(Boolean);

  // ===== Status-aware eligibility (filter once, reuse everywhere) =====
  const assignableRolesWhenSubmitted: Array<MemberWithUser['role']> = ['ADMIN', 'OWNER'];

  // Auditors are never assignment candidates; viewers require an explicit opt-in.
  let filteredMembers = (members || []).filter(
    (m) => m.role !== 'AUDITOR' && (includeViewers || m.role !== 'VIEWER')
  );

  // State-specific filters
  if (taskStatus === 'SUBMITTED') {
    // For SUBMITTED: only Admin/Owner (already excludes viewer)
    filteredMembers = filteredMembers.filter((m) => assignableRolesWhenSubmitted.includes(m.role));
  }

  const matchesSearch = (member: MemberWithUser) => {
    if (!search) return true;
    const term = search.toLowerCase().trim();
    return (
      member.user?.name?.toLowerCase().includes(term) ||
      member.user?.email?.toLowerCase().includes(term) ||
      getEmailPrefix(member.user?.email)?.toLowerCase().includes(term)
    );
  };

  const visibleMembers = filteredMembers.filter(matchesSearch);

  // Separate current user, current assignee, and team members (all based on filteredMembers)
  const currentUser = visibleMembers.find((m) => m.userId === currentUserId);
  const currentAssignee = assignee
    ? visibleMembers.find((m) => m.userId === assignee.id)
    : null;
  // Team members: not current user, not current assignee
  const otherMembers = visibleMembers.filter(
    (m) => m.userId !== currentUserId && m.userId !== assignee?.id
  ) || [];
  // Check if current assignee is the current user
  const isAssigneeCurrentUser = assignee?.id === currentUserId;

  // Assign mutation
  const assignMutation = useMutation({
    ...trpc.guidedTemplate.updateAssignment.mutationOptions(),
    onSuccess: (_, variables) => {
      const assignedMember = members.find((m: MemberWithUser) => m.userId === variables.assigneeId);
      toast.success(
        variables.assigneeId
          ? `Assigned to ${assignedMember?.user?.name || 'member'}`
          : 'Assignment removed'
      );
      // Invalidate controls tree to update assignment indicator
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey(),
      });
      // Invalidate status query to update test detail page
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getStatusById.queryKey({ id: statusId }),
      });
      // Invalidate control detail page to update owner display
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlDetail.queryKey(),
      });
      // Invalidate review queue so assignment updates reflect without manual reload
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getReviewQueue.queryKey(),
      });
      // Invalidate subtask list (for subtasks displayed in SubtasksList)
      queryClient.invalidateQueries({
        queryKey: trpc.subtask.list.queryKey(),
      });
      if (statusId) {
        queryClient.invalidateQueries({
          queryKey: trpc.audit.getActivityFeed.infiniteQueryKey({
            targetType: 'control_evidence_status',
            targetId: statusId,
          }),
        });
      }
      setOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to assign: ${error.message}`);
    },
  });

  const handleAssign = (userId: string | null) => {
    if (onValueChange) {
      onValueChange(userId || null);
      setOpen(false);
      return;
    }
    assignMutation.mutate({
      statusId,
      assigneeId: userId || null,
    });
  };

  // Wrap a row in a HoverCard only when enabled — callers on tight
  // surfaces (e.g. task cards in a 320px column) pass showHoverCard=false
  // to avoid a secondary flyout while browsing the list.
  const MemberRow = ({
    preview,
    children,
  }: {
    preview: React.ReactNode;
    children: React.ReactNode;
  }) => {
    if (!showHoverCard) return <>{children}</>;
    return (
      <HoverCard openDelay={300}>
        <HoverCardTrigger asChild>{children}</HoverCardTrigger>
        <HoverCardContent side="right" align="start" className="w-64">
          {preview}
        </HoverCardContent>
      </HoverCard>
    );
  };

  const comboboxData = visibleMembers.map((member: MemberWithUser) => ({
    label: member.user?.name || member.user?.email || 'Unknown',
    value: member.userId,
  }));

  const isAssigneeVera =
    !!assignee?.isServiceAccount || (!!agentUser && assignee?.id === agentUser.id);
  const fullAssigneeDisplayName = isAssigneeVera
    ? 'Vera'
    : assignee?.name || getEmailPrefix(assignee?.email) || 'Unknown';
  const compactAssigneeDisplayName = isAssigneeVera
    ? 'Vera'
    : fullAssigneeDisplayName.split(/\s+/)[0];
  const assigneeDisplayName =
    assigneeDisplayVariant === 'compact'
      ? compactAssigneeDisplayName
      : fullAssigneeDisplayName;
  const showAssigneeSecondary =
    assigneeDisplayVariant === 'full' && (!!assignee?.email || isAssigneeVera);
  const compactAssigneeLabel = showName && assigneeDisplayVariant === 'compact' && (
    <span className="truncate text-xs leading-none">
      {assigneeDisplayName}
    </span>
  );
  const customTrigger = isValidElement<{
    onClick?: React.MouseEventHandler<HTMLElement>;
  }>(children) && children.type !== Fragment
    ? cloneElement(children, {
        onClick: (event) => {
          children.props.onClick?.(event);
          event.stopPropagation();
        },
      })
    : (
        <div
          role="button"
          tabIndex={0}
          className={className || "inline-flex items-center"}
          onClick={(event) => event.stopPropagation()}
        >
          {children}
        </div>
      );

  // Read-only mode - just display the assignee without interaction
  if (readOnly) {
    if (!assignee) {
      // No assignee in read-only mode - show nothing or minimal indicator
      if (!showName) return null;
      return (
        <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
          <User className="h-3 w-3" />
          Unassigned
        </span>
      );
    }

    return (
      <div className={showName ? "inline-flex items-center gap-1.5" : "inline-flex items-center"}>
        <MemberAvatar person={{ ...assignee, isAgent: isAssigneeVera }} size="sm" />
        {compactAssigneeLabel}
        {showName && assigneeDisplayVariant === 'full' && (
          <div className={assigneeDisplayVariant === 'compact' ? "min-w-0" : "flex flex-col min-w-0"}>
            <div className="flex items-center gap-1">
              <span className="text-xs leading-none truncate">
                {assigneeDisplayName}
              </span>
              {isAssigneeCurrentUser && (
                <Badge variant="secondary" className="text-[10px] px-1 py-0 h-3.5 font-normal shrink-0">You</Badge>
              )}
            </div>
            {showAssigneeSecondary && (
              <span className="text-[10px] text-muted-foreground truncate">
                {isAssigneeVera ? 'AI compliance agent' : assignee.email}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <Combobox
        data={comboboxData}
        type="assignee"
        modal={modal}
        open={open}
        onOpenChange={setOpen}
        value={currentAssigneeId || ''}
        onValueChange={handleAssign}
      >
        <ComboboxTrigger asChild variant="ghost" className="!p-0 !h-auto !border-0 !shadow-none !bg-transparent">
          {children ? (
            customTrigger
          ) : trigger === 'icon' ? (
            assignee ? (
              // If assigned, show avatar (and optionally firstname) - Vercel style
              <button
                type="button"
                className={showName
                  ? "inline-flex items-center gap-1.5"
                  : "inline-flex items-center justify-center"
                }
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <MemberAvatar person={{ ...assignee, isAgent: isAssigneeVera }} size="sm" />
                {compactAssigneeLabel}
                {showName && assigneeDisplayVariant === 'full' && (
                  <div className={assigneeDisplayVariant === 'compact' ? "min-w-0 text-left" : "flex flex-col min-w-0 text-left"}>
                    <div className="flex items-center gap-1">
                      <span className="text-xs leading-none truncate">
                        {assigneeDisplayName}
                      </span>
                      {isAssigneeVera && (
                        <Badge variant="secondary" className="text-[10px] px-1 py-0 h-3.5 font-normal shrink-0">AI</Badge>
                      )}
                      {isAssigneeCurrentUser && !isAssigneeVera && (
                        <Badge variant="secondary" className="text-[10px] px-1 py-0 h-3.5 font-normal shrink-0">You</Badge>
                      )}
                    </div>
                    {showAssigneeSecondary && (
                      <span className="text-[10px] text-muted-foreground truncate">
                        {isAssigneeVera ? 'AI compliance agent' : assignee.email}
                      </span>
                    )}
                  </div>
                )}
              </button>
            ) : (
              // If not assigned, show UserPlus icon (and optionally "Assign" text)
              <button
                type="button"
                className={className || (showName
                  ? "inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  : "inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
                )}
                title="Assign"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <UserPlus className={showName ? "h-4 w-4" : "h-3 w-3"} />
                {showName && <span className="font-heading">Assign</span>}
              </button>
            )
          ) : (
            <Button
              variant="outline"
              size="sm"
              className={className}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <UserPlus className="h-4 w-4" />
              Assign
            </Button>
          )}
        </ComboboxTrigger>

        <ComboboxContent
          className={modal ? 'z-[60]' : undefined}
          popoverOptions={{
            ...(modal ? { portalContainer: null } : {}),
            align: 'end',
            side: contentSide,
            sideOffset: 8,
            style: {
              width: typeof contentWidth === 'number' ? `${contentWidth}px` : contentWidth,
            },
          }}
        >
          <ComboboxInput
            placeholder="Search members..."
            value={search}
            onValueChange={(value) => setSearch(value)}
          />
          <ComboboxList>
            <ScrollArea type="always" viewClassName="max-h-[280px] h-full [&>div]:!block">            
              <ComboboxEmpty>No members found</ComboboxEmpty>
              <ComboboxGroup>
                {allowUnassigned && (
                  <ComboboxItem
                    value="__none__"
                    onSelect={() => handleAssign(null)}
                  >
                    <div className="flex items-center flex-1 min-w-0">
                      <Avatar className="h-5 w-5 mr-2">
                        <AvatarFallback className="bg-muted">
                          <User className="h-3 w-3 text-muted-foreground" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm">No assignee</span>
                      </div>
                    </div>
                  </ComboboxItem>
                )}

                {/* Current user */}
                {currentUser && (
                  <ComboboxItem
                    value={currentUser.userId}
                    keywords={getMemberKeywords(currentUser)}
                    onSelect={() => handleAssign(currentUser.userId)}
                  >
                    <MemberRow
                      preview={
                        <div className="flex items-start gap-3">
                          <MemberAvatar person={currentUser.user} size="md" />
                          <div className="space-y-1">
                            <p className="text-sm font-medium">{currentUser.user?.name || getEmailPrefix(currentUser.user?.email) || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground">{currentUser.user?.email}</p>
                          </div>
                        </div>
                      }
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <MemberAvatar person={currentUser.user} size="sm" className="mr-2" />
                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-sm">
                              {currentUser.user?.name || getEmailPrefix(currentUser.user?.email) || 'Unknown'}
                            </span>
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-normal">You</Badge>
                          </div>
                          <span className="truncate text-xs text-muted-foreground">
                            {currentUser.user?.email}
                          </span>
                        </div>
                      </div>
                    </MemberRow>
                    {currentAssigneeId === currentUser.userId && (
                      <Check className="h-4 w-4 ml-auto text-primary" />
                    )}
                  </ComboboxItem>
                )}

                {/* Current assignee (if different from current user) */}
                {currentAssignee && currentAssignee.userId !== currentUserId && (
                  <ComboboxItem
                    value={currentAssignee.userId}
                    keywords={getMemberKeywords(currentAssignee)}
                    onSelect={() => handleAssign(currentAssignee.userId)}
                  >
                    <MemberRow
                      preview={
                        <div className="flex items-start gap-3">
                          <MemberAvatar person={currentAssignee.user} size="md" />
                          <div className="space-y-1">
                            <p className="text-sm font-medium">
                              {currentAssignee.user?.name || getEmailPrefix(currentAssignee.user?.email) || 'Unknown'}
                            </p>
                            <p className="text-xs text-muted-foreground">{currentAssignee.user?.email}</p>
                          </div>
                        </div>
                      }
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <MemberAvatar person={currentAssignee.user} size="sm" className="mr-2" />
                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-sm">
                              {currentAssignee.user?.name || getEmailPrefix(currentAssignee.user?.email) || 'Unknown'}
                            </span>
                          </div>
                          <span className="truncate text-xs text-muted-foreground">
                            {currentAssignee.user?.email}
                          </span>
                        </div>
                      </div>
                    </MemberRow>
                    {currentAssigneeId === currentAssignee.userId && (
                      <Check className="h-4 w-4 ml-auto text-primary" />
                    )}
                  </ComboboxItem>
                )}
              </ComboboxGroup>

              {/* Vera agent */}
              {!excludeAgent && agentUser && agentUser.id !== currentAssigneeId && (
                <>
                  <ComboboxSeparator />
                  <ComboboxGroup heading="Agents">
                    <ComboboxItem
                      value={agentUser.id}
                      keywords={['vera', 'ai', 'agent']}
                      onSelect={() => handleAssign(agentUser.id)}
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <MemberAvatar person={{ isAgent: true }} size="sm" className="mr-2" />
                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-sm">Vera</span>
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-normal">AI</Badge>
                          </div>
                          <span className="truncate text-xs text-muted-foreground">
                            AI compliance agent
                          </span>
                        </div>
                      </div>
                      {currentAssigneeId === agentUser.id && (
                        <Check className="h-4 w-4 ml-auto text-primary" />
                      )}
                    </ComboboxItem>
                  </ComboboxGroup>
                </>
              )}

              {/* Other team members */}
              {otherMembers.length > 0 && (
                <>
                  <ComboboxSeparator />
                  <ComboboxGroup heading="Team members">
                    {otherMembers.map((member: MemberWithUser) => (
                      <ComboboxItem
                        key={member.userId}
                        value={member.userId}
                        keywords={getMemberKeywords(member)}
                        onSelect={() => handleAssign(member.userId)}
                      >
                        <MemberRow
                          preview={
                            <div className="flex items-start gap-3">
                              <MemberAvatar person={member.user} size="md" />
                              <div className="space-y-1">
                                <p className="text-sm font-medium">{member.user?.name || getEmailPrefix(member.user?.email) || 'Unknown'}</p>
                                <p className="text-xs text-muted-foreground">{member.user?.email}</p>
                              </div>
                            </div>
                          }
                        >
                          <div className="flex items-center flex-1 min-w-0">
                            <MemberAvatar person={member.user} size="sm" className="mr-2" />
                            <div className="flex flex-col min-w-0">
                              <span className="truncate text-sm">
                                {member.user?.name || getEmailPrefix(member.user?.email) || 'Unknown'}
                              </span>
                              <span className="truncate text-xs text-muted-foreground">
                                {member.user?.email}
                              </span>
                            </div>
                          </div>
                        </MemberRow>
                        {currentAssigneeId === member.userId && (
                          <Check className="h-4 w-4 ml-auto text-primary" />
                        )}
                      </ComboboxItem>
                    ))}
                  </ComboboxGroup>
                </>
              )}
            </ScrollArea>

          </ComboboxList>
        </ComboboxContent>
      </Combobox>

    </>
  );
}
